import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import { FieldErrors, UseFormWatch } from 'react-hook-form'
import { useTranslation } from 'next-i18next'
import { useGetStatesQuery, usePrefetchStateMethods } from 'redux/services/states'

import Row from 'modules/ui/atoms/Layout/Row'
import Column from 'modules/ui/atoms/Layout/Column'
import SelectField from 'modules/ui/molecules/FormComponents/SelectField'
import Label from 'modules/ui/atoms/Typography/Label'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'

import InputField from 'modules/ui/molecules/FormComponents/InputField'
import { STRIPE_COUNTRY_CODES } from 'utils/constants/misc'
import { DedicationFormData } from './order-dedication-drawer-panel'

interface OrderDedicationAddressFieldsProps {
  watch: UseFormWatch<DedicationFormData>
  errors: FieldErrors
}

const OrderDedicationAddressFields = (props: OrderDedicationAddressFieldsProps) => {
  const { errors, watch } = props
  const { t } = useTranslation()

  const watchCountry = watch('recipientCountry')

  const countries = useMemo(() => {
    return STRIPE_COUNTRY_CODES.map(({ country, code }) => ({ value: code, label: country }))
  }, [])

  usePrefetchStateMethods('US')
  usePrefetchStateMethods('CA')

  const { data: states, isFetching: statesIsFetching } = useGetStatesQuery({
    countryCode: watchCountry,
  })

  const StateField = () => {
    let field = null

    if (statesIsFetching) {
      const StyledStateField = styled.div`
        margin-top: 12px;
      `

      field = (
        <StyledStateField>
          <Label size="sm">
            {t('OrderDedicationAddressFields.fieldLabels.state', 'State')}
            {' *'}
          </Label>
          <Placeloader
            style={{ minHeight: '46px', borderRadius: '8px', margin: '4px 0', width: '100%' }}
          />
        </StyledStateField>
      )
    } else {
      const hasStates = (states?.length ?? 0) > 0

      let label = ''

      switch (watchCountry) {
        case 'US':
          label = t('OrderDedicationAddressFields.fieldLabels.usState', 'State')
          break
        case 'CA':
          label = t('OrderDedicationAddressFields.fieldLabels.caProvince', 'Province')
          break
        default:
          label = t('OrderDedicationAddressFields.fieldLabels.region', 'Region')
      }

      const options = (states ?? []).map(({ name, isoCode }) => ({
        value: isoCode,
        label: name,
      }))

      options.unshift({
        value: '',
        label: t('OrderDedicationAddressFields.fieldLabels.pleaseSelect', 'Please select'),
      })

      field = (
        <SelectField
          required={hasStates}
          id="recipientState"
          label={label}
          error={errors.recipientState as { message: string }}
          options={options}
          dataId="dedication-recipient-state-select"
        />
      )
    }

    return field
  }

  return (
    <div>
      <h4>
        {t(
          'OrderDedicationAddressFields.title.whereToSendPrintedCard',
          'Where do we send the printed card?',
        )}
      </h4>

      <Row>
        <Column>
          <InputField
            id="recipientAddress"
            label={t('OrderDedicationAddressFields.fieldLabels.address', 'Address')}
            dataId="dedication-recipient-address-line1-input"
            error={errors.recipientAddress as { message: string }}
            required
          />
        </Column>
      </Row>

      <Row>
        <Column>
          <InputField
            id="recipientPostalCode"
            dataId="dedication-recipient-address-postalcode-input"
            label={
              watchCountry == 'US'
                ? t('OrderDedicationAddressFields.fieldLabels.zipCode', 'Zip Code')
                : t('OrderDedicationAddressFields.fieldLabels.postalCode', 'Postal Code')
            }
            error={errors.recipientPostalCode as { message: string }}
            required
          />
        </Column>
        <Column>
          <InputField
            id="recipientCity"
            label={t('OrderDedicationAddressFields.fieldLabels.city', 'City')}
            dataId="dedication-recipient-address-city-input"
            error={errors.recipientCity as { message: string }}
            required
          />
        </Column>
      </Row>

      <RowStyled>
        <Column style={{ marginRight: 10 }}> {StateField()}</Column>
        <Column>
          <SelectField
            required
            id="recipientCountry"
            label={t('OrderDedicationAddressFields.fieldLabels.country', 'Country')}
            dataId="dedication-recipient-address-country-select"
            error={errors.recipientCountry as { message: string }}
            options={countries}
          />
        </Column>
      </RowStyled>
    </div>
  )
}

export default OrderDedicationAddressFields

const RowStyled = styled.div`
  display: flex;
  flex-direction: row;

  // Media query for mobile
  @media (max-width: 767px) {
    flex-direction: column;

    > :nth-child(1) {
      margin-right: 0 !important;
    }

    > :nth-child(2) {
      width: 50%;
    }
  }
`
